import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from '../Modal'
import { getWeekDetail } from '../../api/client'
import { currentLocale } from '../../i18n'

/**
 * Which week a discrepancy comes from -- opened by clicking a person's name
 * (their hours per week across every project) or a project's column header
 * (every person's hours per week on that project) in the monthly table.
 *
 * Built on `availability.build_week_detail`, the same source the monthly
 * table itself reads (`booked_hours`), so a total shown here can never
 * disagree with the row or column it was opened from.
 */
const num = (v, digits = 1) =>
  v == null ? '—' : new Intl.NumberFormat(currentLocale(), {
    maximumFractionDigits: digits }).format(v)

const weekLabel = (iso, index) => {
  const d = new Date(iso + 'T00:00:00')
  const dd = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`
  return `S${index + 1} · ${dd}`
}

const SOURCE_DOT = {
  sent: 'bg-emerald-500', live: 'bg-blue-500', committed: 'bg-amber-500', none: 'bg-gray-300',
}

export default function WeekDetailModal({ year, month, mode, id, name, onClose }) {
  const { t } = useTranslation('clevel')
  const [data, setData] = useState(null)
  const [error, setError] = useState('')

  const k = (suffix, opts) => t(`capacity.weekDetail.${suffix}`, opts)

  useEffect(() => {
    let cancelled = false
    getWeekDetail(year, month, mode === 'project' ? { projectId: id } : { teamMemberId: id })
      .then(d => { if (!cancelled) setData(d) })
      .catch(err => {
        if (!cancelled) setError(err.response?.data?.detail || k('loadFailed'))
      })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, mode, id])

  const title = mode === 'project' ? k('titleProject', { name }) : k('titlePerson', { name })

  return (
    <Modal title={title} onClose={onClose} wide>
      <div style={{ minWidth: 'min(720px, 85vw)' }}>
        {error && <p className="text-red-600 text-sm bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
        {!error && !data && <p className="text-sm text-gray-400 py-6">{t('capacity.loading')}</p>}
        {data && (
          <>
            <p className="text-xs text-gray-500 mb-3">
              {mode === 'project' ? k('subtitleProject') : k('subtitlePerson')}
            </p>
            <div className="overflow-auto max-h-[60vh] rounded-lg border border-gray-200">
              <table className="w-full text-xs border-separate border-spacing-0">
                <thead>
                  <tr>
                    <th className="sticky top-0 z-10 bg-white border-b border-gray-200 text-left px-3 py-2 font-semibold text-gray-600">
                      {mode === 'project' ? k('person') : k('project')}
                    </th>
                    {data.weeks.map((w, i) => (
                      <th key={w.monday}
                          className="sticky top-0 z-10 bg-white border-b border-gray-200 px-2 py-2 font-medium text-gray-500 text-right whitespace-nowrap">
                        {weekLabel(w.monday, i)}
                      </th>
                    ))}
                    <th className="sticky top-0 z-10 bg-white border-b border-gray-200 px-2 py-2 font-semibold text-gray-600 text-right whitespace-nowrap">
                      {k('total')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row, i) => (
                    <tr key={row.id} className={i % 2 ? 'bg-gray-50/60' : 'bg-white'}>
                      <td className="px-3 py-1.5 text-gray-800 whitespace-nowrap">
                        {row.code && <span className="text-gray-400 mr-1.5">{row.code}</span>}
                        {row.name}
                      </td>
                      {data.weeks.map(w => {
                        const cell = row.cells[w.monday]
                        return (
                          <td key={w.monday} className="px-2 py-1.5 text-right tabular-nums text-gray-700">
                            {cell ? (
                              <span className="inline-flex items-center gap-1">
                                <span className={`inline-block w-1.5 h-1.5 rounded-full ${SOURCE_DOT[cell.source]}`}
                                      title={t(`capacity.sources.${cell.source}`)} />
                                {num(cell.hours)}
                              </span>
                            ) : '—'}
                          </td>
                        )
                      })}
                      <td className="px-2 py-1.5 text-right tabular-nums font-medium text-gray-900">
                        {num(row.total)}
                      </td>
                    </tr>
                  ))}
                  {data.rows.length === 0 && (
                    <tr>
                      <td colSpan={data.weeks.length + 2} className="px-4 py-8 text-center text-gray-400">
                        {k('noHours')}
                      </td>
                    </tr>
                  )}
                </tbody>
                {data.rows.length > 0 && (
                  <tfoot>
                    <tr className="bg-gray-50 font-semibold">
                      <td className="px-3 py-2 text-gray-900 border-t border-gray-300">
                        {k('totalRow')}
                      </td>
                      {data.weeks.map(w => (
                        <td key={w.monday}
                            className="px-2 py-2 text-right tabular-nums text-gray-800 border-t border-gray-300">
                          {num(data.totals.by_week[w.monday])}
                        </td>
                      ))}
                      <td className="px-2 py-2 text-right tabular-nums text-gray-900 border-t border-gray-300">
                        {num(data.totals.total)}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            <p className="text-[11px] text-gray-400 mt-2">{k('legend')}</p>
          </>
        )}
      </div>
    </Modal>
  )
}
